// src/pages/DictPage/components/UpgradeDialog.tsx
import {
    Button,
    DialogActions,
    DialogContent,
    Stack,
    TextField,
    Grid,
    Typography,
    Divider,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import DialogHeader from "../../../components/Dialog/DialogHeader";
import UserDialog from "../../../components/Dialog/UserDialog";
import {
    WeaponUpgradeFullDto,
    CategoryPrices
} from "../../../model/Dto/Dict/WeaponUpgrade";

interface UpgradeDialogProps {
    open: boolean;
    onClose: () => void;
    onSave: (data: {
        name: string;
        description: string;
        price: number;
        rarity?: number | null;
        prices?: CategoryPrices | null;
    }) => Promise<boolean>;
    initialData?: WeaponUpgradeFullDto | null;
    title: string;
}

export default function UpgradeDialog({
                                          open,
                                          onClose,
                                          onSave,
                                          initialData,
                                          title,
                                      }: UpgradeDialogProps) {
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [price, setPrice] = useState('0');
    const [rarity, setRarity] = useState('');
    const [hasCategoryPrices, setHasCategoryPrices] = useState(false);
    const [prices, setPrices] = useState<Record<keyof CategoryPrices, string>>({
        firstCategory: '0',
        secondCategory: '0',
        thirdCategory: '0',
        fourthCategory: '0',
        fifthCategory: '0',
    });

    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (initialData) {
            setName(initialData.name);
            setDescription(initialData.description);
            setPrice(String(initialData.price));
            setRarity(initialData.rarity === null ? '' : String(initialData.rarity));
            setHasCategoryPrices(!!initialData.prices);
            if (initialData.prices) {
                setPrices({
                    firstCategory: String(initialData.prices.firstCategory),
                    secondCategory: String(initialData.prices.secondCategory),
                    thirdCategory: String(initialData.prices.thirdCategory),
                    fourthCategory: String(initialData.prices.fourthCategory),
                    fifthCategory: String(initialData.prices.fifthCategory),
                });
            }
        } else {
            setName("");
            setDescription("");
            setPrice('0');
            setRarity('');
            setHasCategoryPrices(false);
            setPrices({
                firstCategory: '0',
                secondCategory: '0',
                thirdCategory: '0',
                fourthCategory: '0',
                fifthCategory: '0',
            });
        }
    }, [initialData, open]);

    const handleSave = async () => {
        setLoading(true);
        const success = await onSave({
            name: name.trim(),
            description: description.trim(),
            price: Number(price) || 0,
            rarity: rarity === '' ? null : Number(rarity),
            prices: hasCategoryPrices ? {
                firstCategory: Number(prices.firstCategory) || 0,
                secondCategory: Number(prices.secondCategory) || 0,
                thirdCategory: Number(prices.thirdCategory) || 0,
                fourthCategory: Number(prices.fourthCategory) || 0,
                fifthCategory: Number(prices.fifthCategory) || 0,
            } : null,
        });
        setLoading(false);

        if (success) {
            onClose();
        }
    };

    const handlePriceChange = (field: keyof CategoryPrices, value: string) => {
        setPrices(prev => ({ ...prev, [field]: value }));
    };

    return (
        <UserDialog handleClose={onClose} open={open} maxWidth="md">
            <DialogHeader loading={loading} title={title} />
            <DialogContent>
                <Stack spacing={3} sx={{ mt: 2 }}>
                    <TextField
                        autoFocus
                        label="Название"
                        fullWidth
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        disabled={loading}
                    />

                    <TextField
                        label="Описание"
                        fullWidth
                        multiline
                        rows={3}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        disabled={loading}
                    />

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Цена"
                                fullWidth
                                inputProps={{ inputMode: 'numeric' }}
                                value={price}
                                onChange={(e) => setPrice(e.target.value)}
                                disabled={loading}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Редкость (число, 0 или пусто = common)"
                                fullWidth
                                inputProps={{ inputMode: 'numeric' }}
                                value={rarity}
                                onChange={(e) => setRarity(e.target.value)}
                                disabled={loading}
                            />
                        </Grid>
                    </Grid>

                    <Divider />

                    <Stack direction="row" alignItems="center" spacing={2}>
                        <Typography variant="subtitle1">Категории цен:</Typography>
                        <Button
                            variant={hasCategoryPrices ? "contained" : "outlined"}
                            size="small"
                            onClick={() => setHasCategoryPrices(!hasCategoryPrices)}
                            disabled={loading}
                        >
                            {hasCategoryPrices ? "Убрать" : "Добавить"}
                        </Button>
                    </Stack>

                    {hasCategoryPrices && (
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="1 категория (оружие < 20)"
                                    fullWidth
                                    inputProps={{ inputMode: 'numeric' }}
                                    value={prices.firstCategory}
                                    onChange={(e) => handlePriceChange("firstCategory", e.target.value)}
                                    disabled={loading}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="2 категория (оружие 20-49)"
                                    fullWidth
                                    inputProps={{ inputMode: 'numeric' }}
                                    value={prices.secondCategory}
                                    onChange={(e) => handlePriceChange("secondCategory", e.target.value)}
                                    disabled={loading}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="3 категория (оружие 50-99)"
                                    fullWidth
                                    inputProps={{ inputMode: 'numeric' }}
                                    value={prices.thirdCategory}
                                    onChange={(e) => handlePriceChange("thirdCategory", e.target.value)}
                                    disabled={loading}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="4 категория (оружие 100-124)"
                                    fullWidth
                                    inputProps={{ inputMode: 'numeric' }}
                                    value={prices.fourthCategory}
                                    onChange={(e) => handlePriceChange("fourthCategory", e.target.value)}
                                    disabled={loading}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="5 категория (оружие 125+)"
                                    fullWidth
                                    inputProps={{ inputMode: 'numeric' }}
                                    value={prices.fifthCategory}
                                    onChange={(e) => handlePriceChange("fifthCategory", e.target.value)}
                                    disabled={loading}
                                />
                            </Grid>
                        </Grid>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    Отмена
                </Button>
                <Button onClick={handleSave} variant="contained" color="primary" disabled={loading}>
                    {loading ? "Сохранение..." : "Сохранить"}
                </Button>
            </DialogActions>
        </UserDialog>
    );
}